// src/config/customerFields.js

// Simple email, phone, and date helpers
const emailRegex = /\S+@\S+\.\S+/;
const phoneRegex = /^[0-9+\-()\s]{6,20}$/;

const toDate = (v) => {
  if (!v) return null;
  // Accept Excel date serials or ISO-like strings
  if (typeof v === "number") {
    // Excel serial to JS date (approx; adjust if your Excel dates off by timezone)
    const epoch = new Date(Date.UTC(1899, 11, 30));
    const ms = v * 86400000;
    const date = new Date(epoch.getTime() + ms);
    return isNaN(date.getTime()) ? null : date.toISOString();
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const toFloat = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? v.replace(/,/g, "") : v;
  const f = parseFloat(n);
  return isNaN(f) ? null : f;
};

const toInt = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? v.replace(/,/g, "") : v;
  const i = parseInt(n, 10);
  return isNaN(i) ? null : i;
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (v === null || v === undefined) return null;
  const s = String(v).trim().toLowerCase();
  if (["true", "yes", "y", "1"].includes(s)) return true;
  if (["false", "no", "n", "0"].includes(s)) return false;
  return null;
};

const enumOf = (allowed) => (v) => {
  if (v === null || v === undefined || v === "") return null;
  const s = String(v).trim();
  // case-insensitive match
  const match = allowed.find((x) => x.toLowerCase() === s.toLowerCase());
  return match || null;
};

// Add a helper:
const passwordIsValid = (v) => {
  if (v == null || v === "") return true; // field is optional; treat empty as okay
  const s = String(v);
  if (s.length < 8 || s.length > 12) return false;
  if (!/[A-Z]/.test(s)) return false;
  if (!/[a-z]/.test(s)) return false;
  if (!/[0-9]/.test(s)) return false;
  if (!/[^A-Za-z0-9]/.test(s)) return false;
  return true;
};

// Prisma enums (adjust as per your schema)
const GENDERS = ["male", "female", "others"]; // guess; replace with your actual enum
const CREATED_VIA = ["manual", "sFTP"]; // example; adjust

// // Custom row-level validator example: at least one of email or phone
// export const rowLevelValidators = [
//   (row) => {
//     // This runs after mapping/normalize, so we see DB keys
//     const hasEmail = row.email && String(row.email).trim();
//     const hasPhone = row.phone && String(row.phone).trim();
//     if (!hasEmail && !hasPhone) {
//       return "At least one of Email  Phone is required";
//     }

//     return null;
//   },
// ];

// CUSTOMER_FIELDS are your DB fields we allow through upload.
// key = Prisma column, label = display, required = must be mapped and non-empty,
// validate = value-level validation, normalize = convert to type/shape Prisma expects
export const CUSTOMER_FIELDS = [
  {
    key: "uniqueRegID",
    label: "Unique Registration ID",
    required: false,
    info: `
      Optional — if left unmapped, the system generates a unique
      registration ID automatically for each uploaded customer.
    `,
  },
  { 
    key: "first_name", 
    label: "First Name", 
    required: true 
  },
  { 
    key: "last_name", 
    label: "Last Name", 
    required: true 
  },
  {
    key: "email",
    label: "Email",
    required: true,
    validate: (v) => !v || emailRegex.test(String(v)),
  },
  {
    key: "phone",
    label: "Phone",
    required: true,
    validate: (v) => !v || phoneRegex.test(String(v)),
  },
  { key: "program_slug", label: "Program Slug", required: true },
  { key: "client_id", label: "Client ID", required: false },

  {
    key: "password",
    label: "Password",
    required: false,
    validate: passwordIsValid,
    info : `
      Password must be at least 8 characters. \n
      Password must not exceed 12 characters. \n
      Must contain at least one uppercase letter. \n
      Must contain at least one lowercase letter. \n
      Must contain at least one number \n
      Must contain at least one special character. \n
    `
  },
  { 
    key: "address", 
    label: "Address", 
    required: false 
  },

  // Profile
  {
    key: "gender",
    label: "Gender",
    required: false,
    normalize: enumOf(GENDERS),
    info : `
      male \n
      female \n
      others \n
    `
  },
  { 
    key: "dateofbirth", 
    label: "Date of Birth", 
    required: false,
    info : `
     Date : YYYY-MM_DD
    ` 
  }, // string in your schema
  { key: "country", label: "Country", required: false },
  { key: "location", label: "Location", required: false },
  { key: "preferred_location", label: "Preferred Location", required: false },

  // Health
  { key: "height", label: "Height", required: false, normalize: toFloat },
  { key: "weight", label: "Weight", required: false, normalize: toFloat },
  { key: "bloodGroup", label: "Blood Group", required: false },
  { key: "smokingHabit", label: "Smoking Habit", required: false },
  { key: "alcoholIntake", label: "Alcohol Intake", required: false },
  { key: "allergy", label: "Allergy", required: false },
  { key: "currentMedication", label: "Current Medication", required: false },

  // Flags/dates
  // {
  //   key: "firstLogin",
  //   label: "First Login",
  //   required: false,
  //   normalize: toBool,
  // },
  // {
  //   key: "firstLoginDate",
  //   label: "First Login Date",
  //   required: false,
  //   normalize: toDate,
  // },
  // {
  //   key: "benefits_reset_date",
  //   label: "Benefits Reset Date",
  //   required: false,
  //   normalize: toDate,
  // },
  
  { 
    key: "isActive", 
    label: "Is Active", 
    required: false, normalize: toBool,
    info : `
    Active :  "true", "yes", "y", "1" \n
    InActive :   "false", "no", "n", "0" \n
    ` 
  },

  // Value & issuance
  { key: "value", label: "Value", required: false, normalize: toFloat },
  {
    key: "date_of_issuance",
    label: "Date of Issuance",
    required: false,
    normalize: toDate,
  },

  // Card/account identifiers
  { key: "customer_type", label: "Customer Type", required: false },
  { key: "primarycard", label: "Primary Card", required: false },
  { key: "mnumber", label: "Membership Number", required: false },
  { key: "regCode", label: "Registration Code", required: false },
  {
    key: "card_creation_date",
    label: "Card Creation Date",
    required: false,
    normalize: toDate,
  },
  { key: "accountserno", label: "Account Serial No", required: false },
  { key: "cardserno", label: "Card Serial No", required: false },
  { key: "rmname", label: "RM Name", required: false },

  // BIN/benefits
  { key: "benefit_id", label: "Benefit ID", required: false },
  { key: "bin", label: "BIN", required: false },

  // Signup/source
  { key: "sign_up_source", label: "Signup Source", required: false },
  // { key: "locations", label: "Locations (CSV)", required: false },
  {
    key: "createdVia",
    label: "Created Via",
    required: false,
    normalize: enumOf(CREATED_VIA),
    info : `
      manual, 
      sFTP
    `
  },
  { 
    key: "signUpMethod", 
    label: "Signup Method", 
    required: false,
    info : `
      Card Number \n
      Registration Code \n
    ` 
  },
  { key: "retailPlan", label: "Retail Plan", required: false },
  { key: "pincode", label: "Pincode", required: false },
];
